import {captureException} from '@sentry/browser';

import {isNotEmpty, isNotNil} from 'ramda';

import {appWorkspaceKey, browserStorageKey, OAUTH_CLIENT_ID} from '@dms/config';

import {workspaceInitializeRoute} from '../constants/routes';
import {buildUrl} from './buildUrl';
import {generateOAuthFlow} from './generateOAuthFlow';
import {isLocalDevHost} from './isLocalDevHost';
import {isExternalNavigationClaimed, navigateExternally} from './navigateExternally';
import {writeOAuthFlowCookie} from './writeOAuthFlowCookie';

type RedirectToWorkspaceOptions = {
  currentUserEmail?: string;
};

export const redirectToWorkspace = (options?: RedirectToWorkspaceOptions) => {
  if (isExternalNavigationClaimed()) {
    return;
  }

  const iamUrl = process.env.IAM_URL;

  if (!iamUrl) {
    throw new Error('IAM_URL is not set');
  }

  const url = new URL(iamUrl);

  const redirect_uri = buildUrl(workspaceInitializeRoute, appWorkspaceKey, false);
  const searchParamsConfig = {
    language: localStorage.getItem(browserStorageKey.LAST_KNOWN_LANGUAGE) ?? 'en',
    client_id: OAUTH_CLIENT_ID,
    redirect_uri,
    email: options?.currentUserEmail,
  };

  Object.entries(searchParamsConfig).forEach(([key, value]) => {
    if (isNotNil(value) && isNotEmpty(value)) {
      url.searchParams.set(key, value);
    }
  });
  const legacyUrl = url.toString();

  void navigateExternally(async () => {
    try {
      const hostname = window.location.hostname;
      const parentDomain = hostname.includes('.')
        ? hostname.split('.').slice(1).join('.')
        : hostname;
      if (isLocalDevHost(hostname) && !parentDomain.includes('.')) {
        return legacyUrl;
      }

      const flow = await generateOAuthFlow();
      writeOAuthFlowCookie(flow, redirect_uri);
      url.searchParams.set('code_challenge', flow.challenge);
      url.searchParams.set('code_challenge_method', flow.challengeMethod);
      url.searchParams.set('state', flow.state);
      return url.toString();
    } catch (error) {
      captureException(error);
      return legacyUrl;
    }
  });
};
